import * as React from "react";
import { Link } from "react-router-dom";

import { useTheme } from "../../../providers/ThemeContext";
import { GlobalStyles } from "../../../styles/GlobalStyles";
import { DetailedCardStyles } from "./DetailedCardStyles";
import { Metric } from "../../../types/RandomGlobalTypes";

type Props = {
  title: string;
  image?: string | null;
  alt?: string;
  yearLabel?: string;
  year?: string | number;
  locationLabel?: string;
  location?: string;
  blurb?: string;
  metrics?: Metric[];
  to?: string;
  onClick?: () => void;
  interactive?: boolean;
  /** Radius for the whole card (image + body). Defaults to 12. */
  cardRadius?: number | string;
  /** Radius for the image only (falls back to cardRadius). */
  imgRadius?: number | string;
  className?: string;
  style?: React.CSSProperties;
};

const DetailedCard = ({
  title,
  image,
  alt = title,
  yearLabel,
  year,
  locationLabel,
  location,
  blurb,
  metrics = [],
  to,
  onClick,
  interactive,
  cardRadius,
  imgRadius,
  className,
  style,
}: Props) => {
  const { theme } = useTheme();

  const g = GlobalStyles(theme);
  const s = DetailedCardStyles(theme);

  const isClickable = Boolean((to || onClick) && interactive !== false);
  const [hovered, setHovered] = React.useState(false);

  const Wrapper: React.ElementType = to && isClickable ? Link : "article";
  const wrapperProps = to && isClickable ? { to } : {};

  // Unified radii
  const effectiveCardRadius = cardRadius ?? 12;
  const effectiveImgRadius = imgRadius ?? effectiveCardRadius;

  const hasImage = Boolean(image);

  return (
    <Wrapper
      {...(wrapperProps as any)}
      className={className}
      style={{
        ...g.card,
        ...(isClickable && hovered ? s.cardHover : {}),
        cursor: isClickable ? "pointer" : "default",
        borderRadius: effectiveCardRadius as any,
        overflow: "hidden",
        ...(style || {}),
      }}
      onMouseEnter={isClickable ? () => setHovered(true) : undefined}
      onMouseLeave={isClickable ? () => setHovered(false) : undefined}
      onFocus={isClickable ? () => setHovered(true) : undefined}
      onBlur={isClickable ? () => setHovered(false) : undefined}
      role={!to && isClickable ? "button" : undefined}
      tabIndex={!to && isClickable ? 0 : undefined}
      onKeyDown={
        !to && isClickable
          ? (e: React.KeyboardEvent) => {
              if ((e.key === "Enter" || e.key === " ") && onClick) onClick();
            }
          : undefined
      }
      onClick={to ? undefined : onClick}
    >
      {/* Always render media wrapper to keep space even without image */}
      <div
        style={{
          ...s.previewWrap,
          borderTopLeftRadius: effectiveImgRadius as any,
          borderTopRightRadius: effectiveImgRadius as any,
        }}
        role={!hasImage ? "img" : undefined}
        aria-label={!hasImage ? alt : undefined}
      >
        {hasImage ? (
          <>
            <img
              src={image as string}
              alt={alt}
              loading="lazy"
              style={{
                ...s.preview,
                ...(isClickable && hovered ? s.previewHover : {}),
                borderTopLeftRadius: effectiveImgRadius as any,
                borderTopRightRadius: effectiveImgRadius as any,
              }}
            />
            <span
              style={{
                ...s.previewMask,
                borderTopLeftRadius: effectiveImgRadius as any,
                borderTopRightRadius: effectiveImgRadius as any,
              }}
            />
          </>
        ) : (
          <div style={s.previewPlaceholder} title={alt}>
            <span>{alt}</span>
          </div>
        )}
      </div>

      <div
        style={{
          ...s.body,
          borderBottomLeftRadius: effectiveCardRadius as any,
          borderBottomRightRadius: effectiveCardRadius as any,
        }}
      >
        <div style={s.cardTitle}>{title}</div>

        {(year != null || location) && (
          <div style={s.metaRow}>
            {year != null && (
              <span>
                {yearLabel ? `${yearLabel}: ` : ""}
                {year}
              </span>
            )}
            {year != null && location ? <span>•</span> : null}
            {location && (
              <span>
                {locationLabel ? `${locationLabel}: ` : ""}
                {location}
              </span>
            )}
          </div>
        )}

        {blurb ? <div style={s.blurb}>{blurb}</div> : null}

        {metrics.length > 0 && (
          <div style={s.statsRow}>
            {metrics.map((m, i) => (
              <div key={i} style={s.statPill}>
                <span>{m.label}:</span>
                <span>{m.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default DetailedCard;
